import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, TrendingUp, BookOpen, Star, Search, Lock, ShieldCheck, FlaskConical } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";

export default function Landing() {
  const [tickerData, setTickerData] = useState([
    { s: "BTC", p: "—", c: 0 },
    { s: "ETH", p: "—", c: 0 },
    { s: "AAPL", p: "—", c: 0 },
    { s: "TSLA", p: "—", c: 0 },
    { s: "NVDA", p: "—", c: 0 },
    { s: "SOL", p: "—", c: 0 },
  ]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 pt-16 md:pt-24 pb-12 md:pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7 space-y-8 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#EAE6E1] bg-white text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-[#5C8D73] animate-pulse" />
              <span className="text-[#6B7068] uppercase tracking-[0.2em] font-semibold">
                For beginners. By design.
              </span>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-medium text-[#2A2F2B] leading-[0.95]">
              Charts you can{" "}
              <span className="italic text-[#547A8F]">actually</span> understand.
            </h1>

            <p className="text-lg text-[#6B7068] leading-relaxed max-w-xl">
              UpTick is a calm, friendly home for new traders. Watch real stocks &
              crypto, learn what the lines mean, and let our AI explain charts in
              plain English — no jargon, no pressure.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button
                  data-testid="hero-cta-register"
                  className="rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white text-base px-6 py-6 transition-soft group"
                >
                  Start charting free
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-soft" strokeWidth={2} />
                </Button>
              </Link>
              <Link to="/learn">
                <Button
                  variant="outline"
                  data-testid="hero-cta-learn"
                  className="rounded-full border-[#EAE6E1] text-[#2A2F2B] hover:bg-[#EAE6E1]/40 text-base px-6 py-6"
                >
                  <BookOpen className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  How it works
                </Button>
              </Link>
            </div>

            <div className="flex items-center gap-6 pt-4 text-sm text-[#6B7068]">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#5C8D73]" />
                Live data
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#D18E63]" />
                AI explanations
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#547A8F]" />
                Beginner tooltips
              </div>
            </div>

            <div
              data-testid="trust-ribbon"
              className="inline-flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5 rounded-full border border-[#5C8D73]/30 bg-[#5C8D7315] text-xs"
            >
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#5C8D73]">
                Secure Trade Platform
              </span>
              <span className="flex items-center gap-1.5 text-[#2A2F2B] dark:text-[#E6EDF3]" data-testid="trust-readonly">
                <Lock className="w-3.5 h-3.5 text-[#5C8D73]" strokeWidth={2} />
                Read-only market data
              </span>
              <span className="flex items-center gap-1.5 text-[#2A2F2B] dark:text-[#E6EDF3]" data-testid="trust-custody">
                <ShieldCheck className="w-3.5 h-3.5 text-[#5C8D73]" strokeWidth={2} />
                No funds custody
              </span>
              <span className="flex items-center gap-1.5 text-[#2A2F2B] dark:text-[#E6EDF3]" data-testid="trust-paper">
                <FlaskConical className="w-3.5 h-3.5 text-[#5C8D73]" strokeWidth={2} />
                Paper trading only
              </span>
            </div>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="rounded-3xl bg-white border border-[#EAE6E1] p-6 relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#547A8F]/10 blur-3xl" />
              <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-[#D18E63]/10 blur-3xl" />

              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B7068] font-semibold">
                      Bitcoin
                    </div>
                    <div className="font-mono-ticker text-3xl text-[#2A2F2B] mt-1">$67,432.10</div>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#5C8D73]/10 text-[#5C8D73] text-xs font-mono-ticker font-semibold">
                    <TrendingUp className="w-3 h-3" strokeWidth={2.5} />
                    +2.34%
                  </div>
                </div>

                {/* Mini SVG chart illustration */}
                <svg viewBox="0 0 400 160" className="w-full h-40 mt-4">
                  <defs>
                    <linearGradient id="hg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#5C8D73" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#5C8D73" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,120 L40,110 L80,115 L120,90 L160,95 L200,75 L240,80 L280,55 L320,50 L360,40 L400,30 L400,160 L0,160 Z"
                    fill="url(#hg)"
                  />
                  <path
                    d="M0,120 L40,110 L80,115 L120,90 L160,95 L200,75 L240,80 L280,55 L320,50 L360,40 L400,30"
                    fill="none"
                    stroke="#5C8D73"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <div className="grid grid-cols-3 gap-2 mt-4">
                  {["1D", "1W", "1M"].map((r, i) => (
                    <div
                      key={r}
                      className={`text-center text-xs py-2 rounded-lg font-mono-ticker ${
                        i === 1
                          ? "bg-[#2A2F2B] text-white"
                          : "bg-[#F9F8F6] text-[#6B7068]"
                      }`}
                    >
                      {r}
                    </div>
                  ))}
                </div>

                {/* AI bubble */}
                <div className="mt-5 p-3 rounded-xl bg-gradient-to-br from-[#D18E63]/10 to-[#547A8F]/10 border border-[#D18E63]/20">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-[#D18E63] to-[#547A8F] flex items-center justify-center">
                      <Sparkles className="w-3 h-3 text-white" strokeWidth={2} />
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#547A8F]">
                      UpTick AI
                    </div>
                  </div>
                  <p className="text-xs text-[#2A2F2B] leading-relaxed">
                    BTC moved up steadily this week — that pattern is called an{" "}
                    <span className="italic">uptrend</span>. Past performance doesn't predict the future.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-16">
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {[
            {
              icon: Search,
              title: "Search anything",
              body: "Stocks and crypto in one search bar. Cmd+K to find AAPL, BTC, and more in a flash.",
              color: "#547A8F",
            },
            {
              icon: TrendingUp,
              title: "Charts that breathe",
              body: "Clean area charts with friendly timeframes. No clutter, no overwhelm — just clarity.",
              color: "#5C8D73",
            },
            {
              icon: Sparkles,
              title: "AI breakdowns",
              body: "Tap one button for a plain-English explanation of what the chart is doing.",
              color: "#D18E63",
            },
          ].map((f, i) => (
            <div
              key={f.title}
              className="rounded-2xl bg-white border border-[#EAE6E1] p-7 transition-soft hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(42,47,43,0.06)] animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`feature-${f.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                style={{ backgroundColor: `${f.color}15` }}
              >
                <f.icon className="w-5 h-5" style={{ color: f.color }} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl tracking-tight text-[#2A2F2B] mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-[#6B7068] leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-16 md:py-24">
        <div className="rounded-3xl bg-[#2A2F2B] p-10 md:p-16 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#D18E63]/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#547A8F]/30 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-white leading-tight">
              Built for the{" "}
              <span className="italic text-[#D18E63]">curious</span>, not the cynical.
            </h2>
            <p className="text-white/70 mt-5 text-lg leading-relaxed">
              Make a watchlist, learn the lingo, and start understanding markets at your own pace.
              Free to start, friendly forever.
            </p>
            <Link to="/register">
              <Button
                data-testid="cta-bottom-register"
                className="mt-8 rounded-full bg-white text-[#2A2F2B] hover:bg-[#D18E63] hover:text-white text-base px-6 py-6 transition-soft"
              >
                Create your free account
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#EAE6E1] mt-12">
        <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-[#6B7068]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D18E63]" />
            <span className="font-heading font-bold text-[#2A2F2B]">uptick</span>
            <span>· Educational use only. Not financial advice.</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/learn" className="hover:text-[#2A2F2B]">Learn</Link>
            <Link to="/login" className="hover:text-[#2A2F2B]">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
